import React, { useState, useEffect, useRef, useCallback } from "react";
import { Mark3D } from "./BrandMarks";
import { Reveal } from "./Primitives";

/* The stack as a specification schedule. Grouping keeps a long
   tool list legible as a document rather than a wall.

   On desktop the right column is a fixed display: the list at the
   left advances, the pane stays put and only its contents change. */
const DIVISIONS = [
  {
    name: "Frontend",
    items: [
      { name: "React", role: "Primary interface library", mark: "React" },
      { name: "Next.js", role: "SSR, routing, edge", mark: "Nextdotjs" },
      { name: "TypeScript", role: "Type safety at scale", mark: "Typescript" },
      { name: "JavaScript", role: "ES2023", mark: "Javascript" },
      { name: "Tailwind CSS", role: "Design systems", mark: "Tailwindcss" },
      { name: "GSAP", role: "Scroll and timeline motion", mark: "Greensock" },
      { name: "Three.js", role: "3D scenes on the web", mark: "Threedotjs" },
      { name: "HTML5 / CSS3", role: "Semantics, layout", mark: "Html5" },
    ],
  },
  {
    name: "Mobile & Desktop",
    items: [
      { name: "React Native", role: "iOS and Android", mark: "React" },
      { name: "Expo", role: "Build and delivery", mark: "Expo" },
      { name: "Tauri", role: "Native desktop on a Rust core", mark: "Tauri" },
    ],
  },
  {
    name: "State",
    items: [
      { name: "Redux Toolkit", role: "Predictable global state", mark: "Redux" },
      { name: "Zustand", role: "Lightweight stores", mark: null },
      { name: "Context API", role: "Scoped state", mark: "React" },
    ],
  },
  {
    name: "Backend & Data",
    items: [
      { name: "Supabase", role: "Auth, storage, realtime", mark: "Supabase" },
      { name: "PostgreSQL", role: "Relational data", mark: "Postgresql" },
      { name: "Firebase", role: "Auth, messaging", mark: "Firebase" },
      { name: "Flask", role: "Python services", mark: "Flask" },
      { name: "REST APIs", role: "Contracts and integration", mark: null },
      { name: "Upstash Redis", role: "Edge caching, queues", mark: "Redis" },
    ],
  },
  {
    name: "Python & AI",
    items: [
      { name: "Python", role: "Modelling, tooling", mark: "Python" },
      { name: "PyTorch", role: "Deep learning", mark: "Pytorch" },
      { name: "scikit-learn", role: "Classical ML", mark: "Scikitlearn" },
      { name: "SHAP", role: "Model explainability", mark: null },
      {
        name: "Google Colab",
        role: "Training environment",
        mark: "Googlecolab",
      },
    ],
  },
  {
    name: "Tooling",
    items: [
      { name: "Git / GitHub", role: "Version control", mark: "Github" },
      { name: "Vercel", role: "Deployment", mark: "Vercel" },
      { name: "Figma", role: "Design handoff", mark: "Figma" },
      { name: "Postman", role: "API testing", mark: "Postman" },
    ],
  },
];

/* Flat list so the display can address any specimen by index. */
const FLAT = DIVISIONS.flatMap((div) =>
  div.items.map((item) => ({ ...item, group: div.name }))
);

export const STACK_COUNT = FLAT.length;

const StackIndex = () => {
  const [active, setActive] = useState(0);
  const rowRefs = useRef([]);
  const lockRef = useRef(false); // pointing beats scrolling while hovering

  /* Scroll drives the dial: whichever row sits nearest the reading
     line becomes the one on display. Works on touch too, where there
     is no hover to lean on. */
  useEffect(() => {
    const onScroll = () => {
      if (lockRef.current) return;
      const line = window.innerHeight * 0.42;
      let best = null;
      let bestDist = Infinity;

      rowRefs.current.forEach((el, i) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) return;
        const dist = Math.abs(r.top + r.height / 2 - line);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });

      if (best !== null) setActive(best);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const hover = useCallback((i) => {
    lockRef.current = true;
    setActive(i);
  }, []);

  const unhover = useCallback(() => {
    lockRef.current = false;
  }, []);

  const current = FLAT[active] || FLAT[0];

  let flatIndex = -1;

  return (
    <div className="grid gap-10 lg:grid-cols-[1fr_minmax(0,360px)] lg:gap-20 lg:items-start">
      {/* ---------- The dial ---------- */}
      <div>
        {DIVISIONS.map((div) => (
          <section key={div.name} className="pt-10 first:pt-0">
            <Reveal className="flex items-baseline gap-4 border-b border-rule-2 pb-3 md:gap-6">
              <span className="label text-ink">{div.name}</span>
              <span className="h-px flex-1 bg-[var(--rule)] translate-y-[-3px]" />
            </Reveal>

            {div.items.map((item, i) => {
              flatIndex += 1;
              const idx = flatIndex;
              const isHot = idx === active;

              return (
                <Reveal
                  key={item.name}
                  delay={Math.min(i * 30, 240)}
                  className="border-b border-rule"
                >
                  <div
                    ref={(el) => (rowRefs.current[idx] = el)}
                    onMouseEnter={() => hover(idx)}
                    onMouseLeave={unhover}
                    className="group relative grid grid-cols-1 items-baseline gap-x-4 gap-y-1 py-3.5 md:grid-cols-[minmax(0,15rem)_1fr] md:gap-x-8 md:py-4"
                  >
                    <span
                      className={[
                        "absolute -inset-x-4 md:-inset-x-6 inset-y-0 -z-10 rounded-lg bg-ink/[0.04]",
                        "transition-opacity duration-500 ease-glass",
                        isHot ? "opacity-100" : "opacity-0",
                      ].join(" ")}
                    />

                    <span
                      className={[
                        "flex items-center gap-3 text-[16px] md:text-[19px] font-medium tracking-[-0.025em]",
                        "transition-transform duration-500 ease-glass",
                        isHot ? "md:translate-x-1" : "",
                      ].join(" ")}
                    >
                      {/* Hairline tick that draws in on the active row */}
                      <span
                        className={[
                          "hidden md:block h-px transition-all duration-500 ease-glass",
                          isHot ? "w-4 bg-ink" : "w-0 bg-transparent",
                        ].join(" ")}
                      />
                      {item.name}
                    </span>

                    <span className="text-[13.5px] md:text-[14px] text-ink-70 tracking-[-0.01em]">
                      {item.role}
                    </span>
                  </div>
                </Reveal>
              );
            })}
          </section>
        ))}
      </div>

      {/* ---------- The display ---------- */}
      <Reveal
        delay={120}
        className="hidden lg:block sticky top-[132px]"
        aria-hidden="true"
      >
        <div className="glass relative overflow-hidden rounded-[26px] p-8">
          {/* The mark itself, extruded and turning, in its own colour.
              The pane never moves; only what sits inside it changes. */}
          <div className="relative flex h-[236px] items-center justify-center">
            <span className="pointer-events-none absolute left-0 right-0 top-1/2 h-px bg-[var(--rule)]" />
            <span className="pointer-events-none absolute bottom-0 top-0 left-1/2 w-px bg-[var(--rule)]" />

            <div key={current.name} className="stack-swap relative">
              <Mark3D
                name={current.mark}
                label={current.name}
                size="clamp(6.5rem, 11vw, 9rem)"
              />
            </div>
          </div>

          {/* Readout */}
          <div className="relative border-t border-rule pt-5">
            <p className="label pb-2">{current.group}</p>
            <div key={`${current.name}-read`} className="stack-swap">
              <p className="text-[25px] font-medium leading-[1.05] tracking-[-0.03em] text-ink">
                {current.name}
              </p>
              <p className="pt-2 text-[13.5px] leading-[1.5] text-ink-70">
                {current.role}
              </p>
            </div>
          </div>

        </div>
      </Reveal>
    </div>
  );
};

export default StackIndex;
